<script>
  import dropdown from 'assets/imgs/navbar/dropdown.svg'
  import editProfile from 'assets/imgs/navbar/edit-profile.svg'
  import notificationSettings from 'assets/imgs/navbar/notification-settings.svg'
  import logout from 'assets/imgs/navbar/logout.svg'
  import accountSettings from 'assets/imgs/navbar/account-settings.svg'
  import { onMount, onDestroy } from 'svelte'
  import auth from 'services/auth/authService'
  import { featureFlagStore } from 'stores/featureFlag'
  import { ADMIN as ADMIN_LABELS } from 'constants/textConstants'

  /**
   * @typedef {Object} User?
   * @property {string} picture - URL to the user's profile picture.
   * @property {string} nickname - The user's nickname.
   * @property {string} name - The user's full name.
   * @property {string} email - The user's email address.
   */

  /**
   * @typedef {Object} Props
   * @property {User} user - User object containing profile information.
   * @property {boolean} [hasAdminRole]
   */

  /** @type {Props} */
  let { user, hasAdminRole = false } = $props()
  let enableAdminPanel = $derived($featureFlagStore?.enableAdminPanel)

  function getUserDisplayInitials(actualUser) {
    if (actualUser?.name) {
      const parts = actualUser.name.split(' ')
      if (parts.length > 1) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
      } else {
        return actualUser.name.substring(0, 2).toUpperCase()
      }
    } else if (actualUser?.nickname) {
      return actualUser.nickname.substring(0, 2).toUpperCase()
    } else if (actualUser?.email) {
      return actualUser.email.substr(0, 2).toUpperCase()
    }
    return ''
  }
  let dropdownVisible = $state(false)

  const toggleDropdown = () => {
    dropdownVisible = !dropdownVisible
  }

  const handleClickOutside = event => {
    const dropdown = document.getElementById('dropdown-user')
    if (dropdown && !dropdown.contains(event.target)) {
      dropdownVisible = false
    }
  }

  onMount(() => {
    window.addEventListener('click', handleClickOutside)
  })

  onDestroy(() => {
    window.removeEventListener('click', handleClickOutside)
  })

  async function handleLogout(event) {
    event.preventDefault()
    try {
      await auth.logout()
    } catch (error) {
      console.error('Logout failed', error)
    }
  }
</script>

<style>
  .avatar {
    margin-top: 10px;
  }
  .dropdown-menu {
    max-width: 244px;
  }
  .email-text {
    max-width: 230px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
</style>

<div class="ml-2 avatar">
  <button
    type="button"
    class="flex flex-row items-center text-sm bg-transparent rounded-lg dropdown-menu"
    aria-expanded="false"
    onclick={event => {
      event.stopPropagation()
      toggleDropdown()
    }}
    data-cy="button-dropdown-user"
  >
    <span class="sr-only">Open user menu</span>
    <div
      class="relative inline-flex items-center justify-center min-w-8 w-8 h-8 overflow-hidden bg-petroleum-400 rounded-md mr-1 hover:ring-2 hover:ring-primary-200"
    >
      <span class="font-medium text-primary-100" data-cy="profile-username"> {getUserDisplayInitials(user)}</span>
    </div>
    <img class="w-[14px] h-[14px]" alt="User Options" src={dropdown} />
  </button>
</div>

<div
  class="z-50 absolute top-14 right-0 my-4 text-base list-none bg-white divide-y divide-primary-100 rounded shadow {dropdownVisible
    ? 'block'
    : 'hidden'}"
  id="dropdown-user"
>
  <div class="flex items-center px-4 py-1">
    {#if user?.picture}
      <img class="w-[50px] h-[50px] rounded" alt="User profile avatar" src={user.picture} data-cy="profile-picture" />
    {:else}
      <div class="relative inline-flex items-center justify-center w-12 h-12 overflow-hidden bg-primary-400 rounded-md" data-cy="profile-picture">
        <span class="font-medium text-primary-100"> {getUserDisplayInitials(user)}</span>
      </div>
    {/if}
    <div class="px-4 py-3" role="none" data-cy="profile-userinfo">
      <p class="text-xl text-primary-900 dark:text-white" role="none" data-cy="profile-name">{user?.name || ''}</p>
      <p class="text-sm font-medium text-primary-900 truncate dark:text-primary-300 email-text" role="none" data-cy="profile-email">
        {user?.email || ''}
      </p>
    </div>
  </div>

  <ul class="py-1" role="none">
    <!-- <li>
      <a href={'#'} class="flex px-4 py-2 text-sm text-primary-700 hover:bg-primary-100" role="menuitem" data-cy="profile-account-settings">
        <span class="mx-2">
          <img alt="Account Settings Icon" src={accountSettings} />
        </span>
        <span>Account settings</span></a
      >
    </li>
    <li>
      <a href={'#'} class="flex px-4 py-2 text-sm text-primary-700 hover:bg-primary-100" role="menuitem" data-cy="profile-account-edit">
        <span class="mx-2">
          <img alt="Edit my profile Icon" src={editProfile} />
        </span>
        <span>Edit my profile</span></a
      >
    </li>
    <li>
      <a href={'#'} class="flex px-4 py-2 text-sm text-primary-700 hover:bg-primary-100" role="menuitem" data-cy="profile-notification-settings">
        <span class="mx-2">
          <img alt="Notification Settings Icon" src={notificationSettings} />
        </span>
        <span>Notification settings</span></a
      >
    </li> -->
    {#if hasAdminRole && enableAdminPanel}
      <li>
        <a href="/admin" class="flex px-4 py-2 text-sm text-primary-700 hover:bg-primary-100" role="menuitem" data-cy="admin-menu">
          <span class="mx-2">
            <img alt="Admin Icon" src={accountSettings} />
          </span>
          <span>{ADMIN_LABELS.NAV.MENU.ADMIN}</span>
        </a>
      </li>
    {/if}
    <li>
      <a
        href="/#"
        class="flex px-4 py-2 text-sm text-primary-700 hover:bg-primary-100"
        role="menuitem"
        data-cy="profile-logout"
        onclick={handleLogout}
      >
        <span class="mx-2">
          <img alt="Logout Icon" src={logout} />
        </span>
        <span>Log out</span>
      </a>
    </li>
  </ul>
</div>
